/**
 * skuUtils.js — Módulo Canônico Único para Resolução, Normalização e Derivação de SKUs
 */

/**
 * Normaliza uma string de SKU (remove espaços das pontas e converte para maiúsculas)
 */
export function cleanSku(sku) {
  if (!sku) return "";
  let cleaned = String(sku).trim().toUpperCase();
  // Remove sufixos descritivos de medida comuns (ex: "MEDIDA: 14", "(MEDIDA 14)", "MEDIDA 14")
  cleaned = cleaned
    .replace(/\s*MEDIDA:\s*\d+/gi, "")
    .replace(/\s*\(MEDIDA\s*\d+\)/gi, "")
    .replace(/\s+MEDIDA\s+\d+/gi, "")
    .trim();
  return cleaned;
}

/**
 * Extrai o SKU base limpo removendo o sufixo de tamanho/aro final (ex: "RIN-LIA-Z-15" -> "RIN-LIA-Z")
 */
export function getBaseSku(sku) {
  const cleaned = cleanSku(sku);
  if (!cleaned) return "";

  let parts = cleaned.split("-");
  if (parts.length <= 1) return cleaned;

  // 1. Remove sufixo de aro/medida numérico (ex: "14", "15", "23", "A15")
  const lastPart = parts[parts.length - 1];
  if (/^\d+(\.\d+)?$/.test(lastPart) || /^A\d+$/i.test(lastPart)) {
    parts = parts.slice(0, parts.length - 1);
  }

  // 2. Remove sufixo -Z (zircônia) se presente na mesma passada (ex: "DIR-AUR-Z" -> "DIR-AUR")
  if (parts.length > 1 && parts[parts.length - 1].toUpperCase() === "Z") {
    parts = parts.slice(0, parts.length - 1);
  }

  return parts.join("-");
}

/**
 * Dicionário canônico de Códigos de Formato/Modelo (derivado da aba Codigo_Formato)
 */
export const CODIGO_MODELO = {
  "02": "Anel",
  "03": "Alianca",
  "04": "Pingente",
  "05": "Pingente",
  "06": "Pingente",
  "09": "Alianca",
  "10": "Anel",
  "12": "Pingente",
  "13": "Pingente",
  "14": "Alianca",
  "15": "Anel",
  "16": "Alianca",
  "17": "Pingente",
  "18": "Anel",
  "19": "Anel",
  "20": "Pingente",
  "23": "Anel",
  "24": "Anel",
  "3AK": "Alianca",
  "4AK": "Alianca",
  "4CL": "Alianca",
  "4TQ3": "Alianca",
  "5AK": "Alianca",
  "5CL": "Alianca",
  "5SC": "Alianca",
  "6TQ": "Alianca",
  "7CL": "Anel",
  "ABC": "Abc",
  "ACO": "Corrente",
  "AK4": "Alianca",
  "ALI": "Alianca",
  "ALO": "Corrente",
  "ANL": "Anel",
  "ANT": "Anel",
  "ATL": "Atlas",
  "AUR": "Aurora",
  "BAL": "Corrente",
  "BEL": "Bela",
  "BER": "Berloque",
  "BOL": "Corrente",
  "BOL3": "Alianca",
  "BRI": "Brinco",
  "BRP": "Brinco",
  "CAF": "Berloque",
  "CAR": "Corrente",
  "CEL": "Anel",
  "CHAV": "Anel",
  "CLI": "Corrente",
  "CLT": "Alianca",
  "COM": "Corrente",
  "COR": "Corrente",
  "CT7": "Alianca",
  "DNA": "Anel",
  "DON": "Donna",
  "DOP": "Berloque",
  "EB03": "Alianca",
  "EB09": "Alianca",
  "EB10": "Alianca",
  "EB24": "Brinco",
  "EB98": "Pulseira",
  "EB99": "Pulseira",
  "ECW": "Anel",
  "ELR": "Alianca",
  "ELW": "Anel",
  "EQU": "Corrente",
  "ESC": "Escapulario",
  "ESCM": "Escapulario",
  "GMA": "Corrente",
  "GMT": "Corrente",
  "GOT": "Gota",
  "ITO": "Alianca",
  "JUNHO": "Anel",
  "KIT1": "Alianca",
  "LIA": "Lianora",
  "LOV": "Anel",
  "LUN": "Anel",
  "M1": "Alianca",
  "M2": "Alianca",
  "MDL": "Medalha",
  "MIR": "Mimir",
  "NAV": "Navete",
  "OCT": "Octogonal",
  "ORV": "Orvalia",
  "OVA": "Oval",
  "PAK": "Alianca",
  "PIA": "Pingente",
  "PIE": "Pingente",
  "PIN": "Pingente",
  "PIP": "Pingente",
  "PIR": "Pingente",
  "PIV": "Pingente",
  "POR": "Corrente",
  "PPA": "Alianca",
  "PPT": "Alianca",
  "PTO": "Anel",
  "PUL": "Pulseira",
  "PUP": "Pulseira",
  "RED": "Redondo",
  "RET3": "Alianca",
  "RET4": "Alianca",
  "RET5": "Alianca",
  "RIZ": "Anel",
  "ROS": "Anel",
  "RPS": "Anel",
  "SER": "Berloque",
  "SIN": "Corrente",
  "SJG": "Anel",
  "SOL": "Solitario",
  "STR": "Alianca",
  "TCL": "Alianca",
  "TIP": "Anel",
  "TQL": "Anel",
  "TRI": "Triunfo",
  "TRQ": "Alianca",
  "TRV": "Corrente",
  "TRZ": "Alianca",
  "TTR": "Alianca",
  "TVT": "Alianca",
  "VEG": "Vega",
  "VEN": "Corrente",
  "VGH": "Alianca",
  "VGO": "Alianca",
  "VIT": "Vitoria",
  "VT4": "Alianca",
  "VTL": "Alianca",
  "Z": "Alianca",
  "ZCL": "Anel"
};

/**
 * Extrai o nome do modelo a partir do SKU.
 * Normaliza o SKU, busca prioritariamente pelo 2º segmento (ex: DIR-TRI-Z-18 -> TRI -> Triunfo)
 * e, caso não encontre, realiza o fallback para o 1º segmento. Retorna null se não identificado.
 */
export function getModelFromSku(sku) {
  const cleaned = cleanSku(sku);
  if (!cleaned) return null;

  const parts = cleaned.split("-");

  // Se tiver 2 segmentos e o 1º for um modelo conhecido não-numérico (ex: TRI-18, BER-10, SOL-Z)
  // enquanto o 2º é numérico (aro/medida) ou tipo de pedra (Z=Zircônia, N=Natural), o modelo é o 1º segmento.
  if (
    parts.length === 2 &&
    (/^\d+(\.\d+)?$/.test(parts[1]) || parts[1] === "Z" || parts[1] === "N")
  ) {
    const seg1 = parts[0].trim().toUpperCase();
    if (CODIGO_MODELO[seg1]) {
      return CODIGO_MODELO[seg1];
    }
  }

  if (parts.length >= 2) {
    const seg2 = parts[1].trim().toUpperCase();
    if (CODIGO_MODELO[seg2]) {
      return CODIGO_MODELO[seg2];
    }
  }

  // Fallback: 1º segmento
  if (parts.length >= 1) {
    const seg1 = parts[0].trim().toUpperCase();
    if (CODIGO_MODELO[seg1]) {
      return CODIGO_MODELO[seg1];
    }
  }

  return null;
}

/**
 * Lista canônica de modelos de joias para filtros e agrupamentos na produção e moldes.
 */
export const MODELOS_CANONICOS = [
  "Todos",
  "Pingente",
  "Anel",
  "Berloque",
  "Brinco",
  "Pulseira",
  "Colar / Corrente",
  "Medalha",
  "Outros"
];

/**
 * Normaliza um termo genérico para um dos modelos canônicos.
 */
export function normalizeModeloNome(raw) {
  if (!raw) return "Outros";
  const str = String(raw).trim().toLowerCase();
  if (str.includes("pingente")) return "Pingente";
  if (str.includes("anel") || str.includes("alianca") || str.includes("aliança") || str.includes("solit") || str.includes("aparador")) return "Anel";
  if (str.includes("berloque")) return "Berloque";
  if (str.includes("brinco") || str.includes("argola")) return "Brinco";
  if (str.includes("pulseira") || str.includes("bracelete")) return "Pulseira";
  if (str.includes("corrente") || str.includes("colar") || str.includes("gargantilha") || str.includes("escapul")) return "Colar / Corrente";
  if (str.includes("medalha")) return "Medalha";
  return "Outros";
}

/**
 * Resolve o modelo / tipo de joia canônico a partir do SKU, ficha técnica e catálogo.
 * Ordem de prioridade:
 * 1. config_borracha.tipo_modelo (definido na borracha)
 * 2. tipo_joia (cadastrado em produto_dados)
 * 3. getModelFromSku(sku) (via tabela canônica CODIGO_MODELO)
 * 4. Prefixos de SKU canônicos (PIN, RIN, BER, etc.)
 * 5. Heurísticas de palavras-chave no nome do produto
 */
export function resolveModeloJoia(sku, catalogItem, pdRecord) {
  // 1. Configuração explícita da borracha (direta, em config_borracha ou ficha_tecnica)
  const configModelo =
    pdRecord?.tipo_modelo ||
    pdRecord?.config_borracha?.tipo_modelo ||
    pdRecord?.ficha_tecnica?.config_borracha?.tipo_modelo ||
    catalogItem?.full_data?.config_borracha?.tipo_modelo ||
    catalogItem?.config_borracha?.tipo_modelo;
  if (configModelo && configModelo.trim()) {
    return normalizeModeloNome(configModelo);
  }

  // 2. Campo tipo_joia em produto_dados
  const tipoJoiaNome = pdRecord?.expand?.tipo_joia?.nome || pdRecord?.tipo_joia_nome || pdRecord?.tipo_joia;
  if (tipoJoiaNome && typeof tipoJoiaNome === "string" && isNaN(tipoJoiaNome)) {
    return normalizeModeloNome(tipoJoiaNome);
  }

  // 3. Prefixo canônico do SKU da fábrica (PIN, RIN, DIR, BER, MED, etc.)
  const clean = cleanSku(sku);
  if (clean) {
    const parts = clean.split("-");
    const prefix = parts[0]?.toUpperCase();
    if (["PIN", "PIA", "PIE", "PIP", "PIR", "PIV"].includes(prefix)) return "Pingente";
    if (["RIN", "DIR", "ALI", "ANL", "ANT", "CEL", "DNA", "ECW", "ELW", "LOV", "LUN", "PTO", "RIZ", "ROS", "RPS", "SJG", "SOL", "TIP", "TQL", "ZCL"].includes(prefix)) return "Anel";
    if (["BER", "CAF", "DOP", "SER"].includes(prefix)) return "Berloque";
    if (["BRI", "BRP", "EB24"].includes(prefix)) return "Brinco";
    if (["PUL", "PUP", "EB98", "EB99"].includes(prefix)) return "Pulseira";
    if (["COR", "ACO", "ALO", "BAL", "CAR", "CLI", "COM", "EQU", "ESC", "ESCM", "GMA", "GMT", "POR", "SIN", "TRV", "VEN"].includes(prefix)) return "Colar / Corrente";
    if (["MED", "MDL"].includes(prefix)) return "Medalha";

    // Suporte a prefixos compostos como MED-PIN (Medalha/Pingente)
    if (parts.length > 1) {
      const seg2 = parts[1]?.toUpperCase();
      if (["PIN", "PIA", "PIE"].includes(seg2)) return "Pingente";
      if (["BER"].includes(seg2)) return "Berloque";
      if (["RIN", "DIR", "ALI"].includes(seg2)) return "Anel";
    }
  }

  // 4. Resolução via getModelFromSku (CODIGO_MODELO)
  const modeloFromSku = getModelFromSku(sku);
  if (modeloFromSku) {
    const norm = normalizeModeloNome(modeloFromSku);
    if (norm !== "Outros") return norm;
  }

  // 5. Palavras-chave no título/nome do produto
  const name = (catalogItem?.name || catalogItem?.full_data?.name || pdRecord?.ficha_tecnica?.nome || "").toLowerCase();
  if (name) {
    if (/\b(pingente|pingentes)\b/i.test(name)) return "Pingente";
    if (/\b(anel|anéis|aneis|aliança|alianca|alianças|aliancas|solitário|solitario|aparador)\b/i.test(name)) return "Anel";
    if (/\b(berloque|berloques)\b/i.test(name)) return "Berloque";
    if (/\b(brinco|brincos|argola|argolas)\b/i.test(name)) return "Brinco";
    if (/\b(pulseira|pulseiras|bracelete)\b/i.test(name)) return "Pulseira";
    if (/\b(corrente|correntes|colar|colares|gargantilha|escapulário|escapulario)\b/i.test(name)) return "Colar / Corrente";
    if (/\b(medalha|medalhas)\b/i.test(name)) return "Medalha";
  }

  return "Outros";
}


/**
 * Resolução Canônica de SKU no Catálogo (Match Exato + Match Hierárquico de Pai/Base)
 * 
 * @param {string} sku - O SKU a ser buscado (ex: "RIN-LIA-Z-15", "DIR-AUR-Z-14")
 * @param {Object} catalogMap - O mapa de catálogo de produtos
 * @returns {Object|null} - Objeto do produto resolvido com dados estendidos ou null
 */
export function resolveCatalogSku(sku, catalogMap) {
  const normSku = cleanSku(sku);
  if (!normSku || !catalogMap) {
    return null;
  }

  // Suporte a catalogMap fornecido como Array ou Objeto
  let mapObj = catalogMap;
  if (Array.isArray(catalogMap)) {
    mapObj = {};
    catalogMap.forEach(item => {
      if (!item) return;
      const k = cleanSku(item.sku || item.full_data?.sku || item.model || item.full_data?.model || item.id);
      if (k) mapObj[k] = item;
    });
  }

  if (typeof mapObj !== "object") {
    return null;
  }

  // 1. TENTATIVA EXATA (Match perfeito)
  if (mapObj[normSku]) {
    return mapObj[normSku];
  }

  // 2. TENTATIVA POR SKU BASE (Removendo sufixo numérico de aro/tamanho e -Z em uma passada)
  const baseSkuCandidate = getBaseSku(normSku);
  if (baseSkuCandidate !== normSku && mapObj[baseSkuCandidate]) {
    const parent = mapObj[baseSkuCandidate];
    return {
      ...parent,
      sku: normSku,
      baseSku: baseSkuCandidate,
      isVariation: true
    };
  }

  // 3. TENTATIVA HIERÁRQUICA (Loop reverso cortando APENAS sufixos reais de variação: aro numérico, A15, ou pedra Z/N)
  const parts = normSku.split("-");
  if (parts.length > 1) {
    for (let i = parts.length - 1; i >= 1; i--) {
      const removedParts = parts.slice(i);
      const isOnlyVariationSuffix = removedParts.every(p => {
        const up = p.toUpperCase();
        return /^\d+(\.\d+)?$/.test(up) || /^A\d+(\.\d+)?$/.test(up) || up === "Z" || up === "N";
      });
      if (!isOnlyVariationSuffix) break;

      const parentCandidate = parts.slice(0, i).join("-");
      if (mapObj[parentCandidate]) {
        const parent = mapObj[parentCandidate];
          const parentBaseSku = parent.sku ? getBaseSku(cleanSku(parent.sku)) : getBaseSku(parentCandidate);
          if (parentBaseSku === getBaseSku(parentCandidate)) {
            return {
              ...parent,
              sku: normSku,
              baseSku: getBaseSku(parentCandidate),
              isVariation: true
            };
          }
        }
      }

      // 4. TENTATIVA POR PERMUTAÇÃO DE PARTES (ex: ATL-DIR -> DIR-ATL)
      if (parts.length === 2) {
        const permuted = `${parts[1]}-${parts[0]}`;
        if (mapObj[permuted]) {
          return mapObj[permuted];
        }
        // Busca variações que começam com o permutado + hífen (evita colidir prefixos parciais)
        const matchingKey = Object.keys(mapObj).find(k => k === permuted || k.startsWith(`${permuted}-`));
        if (matchingKey && getBaseSku(cleanSku(matchingKey)) === permuted) {
          return mapObj[matchingKey];
        }
      } else if (parts.length >= 3) {
        const permutedBase = `${parts[1]}-${parts[0]}`;
        const matchingKey = Object.keys(mapObj).find(k => k === permutedBase || k.startsWith(`${permutedBase}-`));
        if (matchingKey && getBaseSku(cleanSku(matchingKey)) === permutedBase) {
          return mapObj[matchingKey];
        }
      }
    }

    return null;
  }

  /**
   * FÓRMULA CANÔNICA DE CARGA REAL DA OFICINA (Fase S3 — S3.1 e S3.2):
   * 
   * Calcula a carga real de trabalho em curso na fábrica, considerando apenas peças ativas em produção.
   * 
   * EXCLUSÕES EXPLÍCITAS:
   *  1. `situacao_fluxo === "suspenso"` (Peças suspensas por erro/falta de insumo)
   *  2. `situacao_fluxo === "interceptado"` ou `status === "pe_interceptado"` (Peças puxadas por pedidos)
   *  3. `status === "pe_pre_escapulario"` (EXCEÇÃO S3.2: Pré-Escapulários pré-fabricados mantidos como estoque intermediário)
   *  4. Status de conclusão (`in_stock`, `sold`, `adjusted_out`)
   */
  export function getActiveWorkshopLoad(inventory) {
    if (!Array.isArray(inventory)) return 0;

    return inventory.filter(item => {
      // Deve ser peça física em fabricação (PE)
      if (!item.isPE) return false;

      // 1. Exclui itens concluídos
      if (["in_stock", "sold", "adjusted_out"].includes(item.status)) return false;

      // 2. Exclui peças suspensas ou interceptadas (S3.1)
      if (item.situacao_fluxo === "suspenso" || item.situacao_fluxo === "interceptado" || item.status === "pe_interceptado") {
        return false;
      }

      // 3. EXCEÇÃO S3.2: Peças em estado pré-fabricado de Pré-Escapulário (estoque intermediário pré-produzido)
      if (item.status === "pe_pre_escapulario") {
        return false;
      }

      return true;
    }).length;
  }

  /**
   * Extrai o número do aro a partir de uma string de SKU ou descrição de medida.
   * Exemplos:
   * - "RIN-LIA-Z-15" -> 15
   * - "DIR-AUR-14" -> 14
   * - "ANL-SOL-A16" -> 16
   * - "AL-CLS-22.5" -> 22.5
   * - "ANEL SOLITARIO MEDIDA 18" -> 18
   * Retorna o número do aro ou null se não houver aro identificável.
   */
  export function extractAroFromSku(sku) {
    if (!sku) return null;
    const raw = String(sku).trim();

    // 1. Procura menções explícitas de medida (ex: "MEDIDA: 14", "MEDIDA 18", "ARO 16")
    const descMatch = raw.match(/(?:MEDIDA|ARO|TAMANHO)\s*:?\s*([0-9]+(?:\.[0-9]+)?)/i);
    if (descMatch) {
      const val = parseFloat(descMatch[1]);
      if (!isNaN(val) && val >= 5 && val <= 40) return val;
    }

    // 2. Analisa os segmentos separados por hífen do SKU limpo
    const cleaned = cleanSku(raw);
    const parts = cleaned.split("-");
    if (parts.length > 1) {
      const lastPart = parts[parts.length - 1];
      // Formato com "A" prefixado (ex: "A16")
      const aMatch = lastPart.match(/^A([0-9]+(?:\.[0-9]+)?)$/i);
      if (aMatch) {
        const val = parseFloat(aMatch[1]);
        if (!isNaN(val) && val >= 5 && val <= 40) return val;
      }
      // Formato numérico puro no último segmento (ex: "15", "22")
      if (/^\d+(\.\d+)?$/.test(lastPart)) {
        const val = parseFloat(lastPart);
        if (!isNaN(val) && val >= 5 && val <= 40) return val;
      }
      // Caso especial: o penúltimo segmento pode ser o aro se o último for "-Z" (ex: "DIR-AUR-16-Z")
      if (parts.length > 2 && lastPart.toUpperCase() === "Z") {
        const prevPart = parts[parts.length - 2];
        if (/^\d+(\.\d+)?$/.test(prevPart)) {
          const val = parseFloat(prevPart);
          if (!isNaN(val) && val >= 5 && val <= 40) return val;
        }
      }
    }

    return null;
  }

  /**
   * Retorna se o modelo de joia utiliza medidas/aros (Anéis e Alianças)
   */
  export function isModeloComMedida(modeloNome) {
    if (!modeloNome) return false;
    const norm = normalizeModeloNome(modeloNome);
    return norm === "Anel";
  }
